import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import { Select } from "@/components/ui/select";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { Link, createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

/**
 * `/projects/:slug/releases/:releaseId/edit` — edit or delete a
 * release. Changes are pushed to GitLab first.
 */

const INPUT_CLS =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

type ReleaseDetailResponse = {
  release: {
    id: string;
    name: string;
    description: string | null;
    dueDate: string | null;
    status: "open" | "locked" | "closed";
    gitlabMilestoneId: number | null;
  };
};

export const Route = createFileRoute("/_app/projects/$slug/releases/$releaseId/edit")({
  loader: async ({ params }) => {
    try {
      return await api.get<ReleaseDetailResponse>(
        `/api/projects/${params.slug}/releases/${params.releaseId}`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) throw notFound();
      throw err;
    }
  },
  component: EditReleasePage,
});

function EditReleasePage() {
  const { t } = useTranslation();
  const { release } = Route.useLoaderData();
  const { slug, releaseId } = Route.useParams();
  const navigate = useNavigate();

  const saveRelease = useApiMutation(
    (body: {
      name: string;
      description: string | null;
      dueDate: string | null;
      status: string;
    }) => api.patch(`/api/projects/${slug}/releases/${releaseId}`, body),
    {
      onSuccess: () =>
        navigate({ to: "/projects/$slug/releases/$releaseId", params: { slug, releaseId } }),
      errorMessage: (err) =>
        err instanceof ApiError ? t("rel.edit.saveForbidden") : t("rel.edit.saveError"),
    },
  );
  const deleteRelease = useApiMutation(
    () => api.delete(`/api/projects/${slug}/releases/${releaseId}`),
    {
      onSuccess: () => navigate({ to: "/projects/$slug/releases", params: { slug } }),
      errorMessage: (err) =>
        err instanceof ApiError ? t("rel.edit.deleteForbidden") : t("rel.edit.deleteError"),
    },
  );
  const error = saveRelease.error ?? deleteRelease.error;

  async function save(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const name = ((fd.get("name") as string) ?? "").trim();
    if (!name) return;
    await saveRelease.mutate({
      name,
      description: ((fd.get("description") as string) ?? "").trim() || null,
      dueDate: ((fd.get("dueDate") as string) ?? "").trim() || null,
      status: fd.get("status") as string,
    });
  }

  async function remove() {
    if (!confirm(t("rel.edit.confirmDelete"))) return;
    await deleteRelease.mutate();
  }

  const dueIso = release.dueDate ? release.dueDate.slice(0, 10) : "";

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-bold">{t("rel.edit.title")}</h2>
        <Button variant="outline" size="sm" asChild>
          <Link to="/projects/$slug/releases/$releaseId" params={{ slug, releaseId }}>
            {t("rel.cancel")}
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{release.name}</CardTitle>
          <CardDescription>
            {t("rel.edit.pushedFirst")}
            {release.gitlabMilestoneId === null && (
              <span className="text-amber-600 block mt-1">{t("rel.edit.notLinked")}</span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium leading-none">
                {t("rel.edit.name")} <span className="text-destructive">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                pattern="\d+\.\d+"
                title={t("rel.edit.nameTitle")}
                defaultValue={release.name}
                className={INPUT_CLS}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="description" className="text-sm font-medium leading-none">
                {t("rel.edit.description")}
              </label>
              <textarea
                id="description"
                name="description"
                rows={4}
                defaultValue={release.description ?? ""}
                className={`${INPUT_CLS} resize-y`}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="dueDate" className="text-sm font-medium leading-none">
                  {t("rel.edit.dueDate")}
                </label>
                <DatePicker id="dueDate" name="dueDate" defaultValue={dueIso} />
              </div>

              <div className="space-y-2">
                <label htmlFor="status" className="text-sm font-medium leading-none">
                  {t("rel.edit.statusLabel")}
                </label>
                <Select
                  name="status"
                  aria-label={t("rel.edit.statusLabel")}
                  defaultValue={release.status}
                  options={[
                    { value: "open", label: t("rel.edit.statusOpen") },
                    { value: "locked", label: t("rel.edit.statusLocked") },
                    { value: "closed", label: t("rel.edit.statusClosed") },
                  ]}
                />
              </div>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={saveRelease.pending}>
                {saveRelease.pending ? t("rel.edit.saving") : t("rel.edit.save")}
              </Button>
              <Button variant="outline" type="button" asChild>
                <Link to="/projects/$slug/releases/$releaseId" params={{ slug, releaseId }}>
                  {t("rel.cancel")}
                </Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive text-base">{t("rel.edit.dangerZone")}</CardTitle>
          <CardDescription>{t("rel.edit.dangerDescription")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            type="button"
            variant="destructive"
            disabled={deleteRelease.pending}
            onClick={remove}
          >
            {deleteRelease.pending ? t("rel.edit.deleting") : t("rel.edit.delete")}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
